import React, { useEffect, useMemo, useRef, useState } from 'react';

import { AppDialog } from '../../../components/shared/AppDialog';
import { readClipboardText } from '../../../utils/clipboard';
import { FormTemplate, countTemplateFields, parseTemplateConfig } from '../templateConfig';

interface PasteTemplateDialogProps {
  open: boolean;
  fallback: Pick<FormTemplate, 'formType' | 'targetDevice'>;
  onClose: () => void;
  onCreate: (template: FormTemplate) => void;
}

const PasteTemplateDialog: React.FC<PasteTemplateDialogProps> = ({
  open,
  fallback,
  onClose,
  onCreate,
}) => {
  const [text, setText] = useState('');
  const [touched, setTouched] = useState(false);
  const [manualPasteHint, setManualPasteHint] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) {
      setText('');
      setTouched(false);
      setManualPasteHint(false);
    }
  }, [open]);

  const result = useMemo(() => parseTemplateConfig(text, fallback), [text, fallback]);
  const showError = touched && Boolean(result.error) && !manualPasteHint;

  const pasteFromClipboard = async () => {
    const value = await readClipboardText();
    if (value === null) {
      setManualPasteHint(true);
      inputRef.current?.focus();
      return;
    }
    setText(value);
    setTouched(true);
  };

  const handleCreate = () => {
    setTouched(true);
    if (result.template) onCreate(result.template);
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title="Create from template config"
      subtitle="Paste a config copied from any template card. The form gets its fields, steps and theme."
      stretchActions={false}
      actions={[
        { label: 'Cancel', variant: 'secondary', onClick: onClose },
        { label: 'Create form', onClick: handleCreate, disabled: !result.template },
      ]}
    >
      <div className="paste-template">
        <div className="paste-template-toolbar">
          <span className="paste-template-label">Template config (JSON)</span>
          <button type="button" className="paste-template-clipboard" onClick={pasteFromClipboard}>
            Paste from clipboard
          </button>
        </div>
        <textarea
          ref={inputRef}
          className={`paste-template-input${showError ? ' has-error' : ''}`}
          value={text}
          spellCheck={false}
          placeholder='{ "type": "form-builder-template", ... }'
          onChange={(e) => {
            setText(e.target.value);
            setManualPasteHint(false);
          }}
          onBlur={() => setTouched(true)}
        />
        {manualPasteHint && (
          <div className="paste-template-hint">
            This page can't read your clipboard. Click in the box and press Ctrl+V (⌘V on Mac).
          </div>
        )}
        {showError && <div className="paste-template-error">{result.error}</div>}
        {result.template && (
          <div className="paste-template-summary">
            <strong>{result.template.name}</strong>
            <span>
              {result.template.formType === 'multistep' ? 'Multi-step' : 'Single page'} ·{' '}
              {countTemplateFields(result.template.configuration)} fields
              {result.template.steps?.length ? ` · ${result.template.steps.length} steps` : ''}
              {result.template.theme ? ' · theme included' : ''}
            </span>
          </div>
        )}
      </div>
    </AppDialog>
  );
};

export default PasteTemplateDialog;
